<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveTicket">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create ticket') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading.form"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Ticket details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Ticket details and classification') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="product">{{ $t('Product') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="product"
                                            v-model="ticket.product_id"
                                            :options="productList"
                                            option-label="name"
                                            :search-fields="['name', 'serial_number']"
                                            required
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <span class="block truncate">
                                                    {{ props.option.name }}<template v-if="props.option.serial_number"> ({{ props.option.serial_number }})</template>
                                                </span>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div>
                                                    <div :class="props.option.id === ticket.product_id ? 'font-semibold' : 'font-normal'" class="block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                    <div v-if="props.option.serial_number || props.option.client" class="text-xs text-gray-600 truncate">
                                                        <span v-if="props.option.serial_number">S/N: {{ props.option.serial_number }}</span>
                                                        <span v-if="props.option.serial_number && props.option.client"> · </span>
                                                        <span v-if="props.option.client">{{ props.option.client.name }}</span>
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                </div>
                                <template v-if="productInfo">
                                    <div v-if="productInfo.client" class="col-span-3">
                                        <label class="inline-flex items-center">
                                            <input v-model="addClientToTicket" type="checkbox" class="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out">
                                            <span class="ml-2 text-sm text-gray-700">{{ $t('Add client to ticket') }}: <b>{{ productInfo.client.name }}</b></span>
                                        </label>
                                    </div>
                                    <div v-if="productInfo.partner" class="col-span-3">
                                        <label class="inline-flex items-center">
                                            <input v-model="addPartnerToTicket" type="checkbox" class="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out">
                                            <span class="ml-2 text-sm text-gray-700">{{ $t('Add partner to ticket') }}: <b>{{ productInfo.partner.name }}</b></span>
                                        </label>
                                    </div>
                                </template>
                                <div v-if="!isEmployee" class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="customer">{{ $t('Customer') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="customer"
                                            v-model="ticket.user_id"
                                            :options="userList"
                                            option-label="name"
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar !== 'gravatar' ? props.option.avatar : props.option.gravatar" alt="Icon" class="w-5 h-auto rounded-full">
                                                    </div>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar !== 'gravatar' ? props.option.avatar : props.option.gravatar" alt="Icon" class="w-5 h-auto rounded-full">
                                                    </div>
                                                    <div :class="props.option.id === ticket.user_id ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="subject">{{ $t('Subject') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="subject"
                                            v-model="ticket.subject"
                                            :placeholder="$t('Subject')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div v-if="!isEmployee" class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="department">{{ $t('Department') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="department"
                                            v-model="ticket.department_id"
                                            :options="departmentList"
                                            option-label="name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="status"
                                            v-model="ticket.status_id"
                                            :options="statusList"
                                            option-label="name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="priority">{{ $t('Priority') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="priority"
                                            v-model="ticket.priority_id"
                                            :options="priorityList"
                                            option-label="name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="ticket_body">{{ $t('Ticket body') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-wysiwyg
                                            id="ticket_body"
                                            v-model="ticket.body"
                                            :cannedReplyList="cannedReplyList"
                                            :plugins="{images: true, cannedReply: true, attachment: true, shortCode: true}"
                                            @selectUploadFile="selectUploadFile"
                                        >
                                            <template v-slot:top>
                                                <div v-for="item in uploadQueue" :key="item.id" class="flex items-center gap-2 mb-2">
                                                    <span class="text-xs text-gray-600 truncate" style="max-width:150px" :title="item.fileName">{{ item.fileName }}</span>
                                                    <span v-if="item.totalBytes" class="text-xs text-gray-400 whitespace-nowrap">
                                                        {{ formatSize(item.uploadedBytes || 0) }} / {{ formatSize(item.totalBytes) }}
                                                    </span>
                                                    <div class="h-1 flex-1 bg-gray-200">
                                                        <div :style="{width: item.percent + '%'}" class="bg-blue-500 py-0.5 transition-all"></div>
                                                    </div>
                                                    <span class="text-xs text-gray-500 whitespace-nowrap">
                                                        <template v-if="item.processing">{{ $t('Processing file...') }}</template>
                                                        <template v-else-if="item.speed > 0">
                                                            {{ item.speed }} MB/s
                                                            <template v-if="item.remaining > 0"> — ~{{ item.remaining }}s</template>
                                                        </template>
                                                        <template v-else>{{ $t('Preparing...') }}</template>
                                                    </span>
                                                    <button v-if="!item.processing" type="button" class="text-red-500 hover:text-red-700 text-sm font-bold px-1" @click="cancelUpload(item.id)">×</button>
                                                </div>
                                            </template>
                                        </input-wysiwyg>
                                    </div>
                                </div>
                                <div v-if="ticket.attachments.length > 0" class="col-span-3">
                                    <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                                        <template v-for="(attachment, index) in ticket.attachments">
                                            <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/tickets"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                                :disabled="uploadQueue.length > 0"
                            >
                                {{ $t('Create ticket') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <input ref="fileInput" hidden type="file" multiple @change="uploadFile($event)">
    </main>
</template>

<script>
import chunkedUpload from '../../../../utils/chunkedUpload';

export default {
    name: "new",
    metaInfo() {
        return {
            title: this.$i18n.t('Create ticket')
        }
    },
    data() {
        return {
            loading: {
                form: true,
            },
            uploadQueue: [],
            ticket: {
                user_id: null,
                product_id: null,
                subject: null,
                department_id: null,
                status_id: 1,
                priority_id: 1,
                body: '',
                attachments: [],
            },
            productInfo: null,
            productList: [],
            addClientToTicket: false,
            addPartnerToTicket: false,
            departmentList: [],
            userList: [],
            statusList: [],
            priorityList: [],
            cannedReplyList: [],
        }
    },
    computed: {
        isEmployee() {
            return this.$store.state.user && this.$store.state.user.role_id === 2;
        }
    },
    watch: {
        'ticket.product_id': function (newVal) {
            if (!newVal) return;
            var found = this.productList.find(function (p) { return p.id === newVal; });
            if (found) {
                this.productInfo = found;
            }
            this.addClientToTicket = false;
            this.addPartnerToTicket = false;
        }
    },
    mounted() {
        const self = this;
        this.getFilters();
        this.getCannedReplies();
        if (this.isEmployee) {
            this.ticket.user_id = this.$store.state.user.id;
            if (this.$route.query.product_id) {
                this.ticket.product_id = parseInt(this.$route.query.product_id);
            }
            this.loadEmployeeProducts();
        } else if (this.$route.query.product_id) {
            this.loadProduct(this.$route.query.product_id);
        } else {
            this.loadEmployeeProducts();
        }
    },
    methods: {
        loadEmployeeProducts() {
            const self = this;
            axios.get('api/dashboard/employee/products', { params: { perPage: 999 } }).then(function (response) {
                self.productList = response.data.items;
                if (self.ticket.product_id) {
                    var found = self.productList.find(function (p) { return p.id === self.ticket.product_id; });
                    if (found) {
                        self.productInfo = found;
                    }
                }
            }).catch(function () {});
        },
        loadProduct(productId) {
            const self = this;
            axios.get('api/dashboard/admin/products/' + productId).then(function (response) {
                self.productInfo = response.data;
                self.ticket.product_id = response.data.id;
                if (!self.isEmployee && response.data.client_id) {
                    self.ticket.user_id = response.data.client_id;
                }
            }).catch(function () {});
        },
        getFilters() {
            const self = this;
            self.loading.form = true;
            axios.get('api/dashboard/tickets/filters').then(function (response) {
                self.userList = response.data.customers;
                self.departmentList = response.data.departments;
                self.statusList = response.data.statuses.map(function (s) {
                    return Object.assign({}, s, { name: self.$i18n.t(s.name) });
                });
                self.priorityList = response.data.priorities.map(function (p) {
                    return Object.assign({}, p, { name: self.$i18n.t(p.name) });
                });
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
            })
        },
        getCannedReplies() {
            const self = this;
            axios.get('api/dashboard/tickets/canned-replies').then(function (response) {
                self.cannedReplyList = response.data;
            })
        },
        saveTicket() {
            const self = this;
            self.loading.form = true;
            var payload = Object.assign({}, self.ticket);
            if (self.isEmployee) {
                payload.add_client = self.addClientToTicket;
                payload.add_partner = self.addPartnerToTicket;
            }
            axios.post('api/dashboard/tickets', payload).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/tickets/' + response.data.ticket.uuid + '/manage');
            }).catch(function () {
                self.loading.form = false;
            });
        },
        selectUploadFile() {
            this.$refs.fileInput.click();
        },
        uploadFile(e) {
            var self = this;
            var files = Array.prototype.slice.call(e.target.files);
            if (!files.length) return;
            if (self.$refs.fileInput) self.$refs.fileInput.value = null;

            files.forEach(function (file) {
                var entry = {
                    id: Date.now() + '_' + Math.random(),
                    fileName: file.name,
                    percent: 0, speed: 0, remaining: 0,
                    processing: false, active: true,
                    uploadedBytes: 0, totalBytes: file.size,
                    cancelSource: axios.CancelToken.source(),
                };
                self.uploadQueue.push(entry);

                chunkedUpload(file, function (progress) {
                    entry.percent = progress.percent;
                    entry.speed = progress.speed;
                    entry.remaining = progress.remaining;
                    entry.processing = progress.processing;
                    entry.active = progress.active;
                    entry.uploadedBytes = progress.uploadedBytes || 0;
                    entry.totalBytes = progress.totalBytes || 0;
                    self.$forceUpdate();
                }, 'api/dashboard/tickets', entry.cancelSource.token).then(function (fileData) {
                    self.ticket.attachments.push(fileData);
                }).catch(function () {}).then(function () {
                    var idx = self.uploadQueue.findIndex(function (q) { return q.id === entry.id; });
                    if (idx !== -1) self.uploadQueue.splice(idx, 1);
                });
            });
        },
        cancelUpload(id) {
            var entry = this.uploadQueue.find(function (q) { return q.id === id; });
            if (entry && entry.cancelSource) {
                entry.cancelSource.cancel('Upload cancelled by user.');
            }
        },
        formatSize(bytes) {
            if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
            if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
            return (bytes / 1024).toFixed(0) + ' KB';
        },
        removeAttachment(index) {
            var attachment = this.ticket.attachments[index];
            if (attachment && attachment.uuid) {
                axios.delete('api/files/' + attachment.uuid).catch(function() {});
            }
            this.ticket.attachments.splice(index, 1);
        }
    },
}
</script>
